import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { useDeleteAccessKey } from 'src/features/accessKey/api/useDeleteAccessKey';
import { LIST_ACCESS_KEYS_RESOURCE } from 'src/features/accessKey/api/useListAccessKeys';
import { ACCESS_KEY_TYPE_TRANSLATION_MAP } from 'src/features/accessKey/utils/constants';
import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { AccessKey } from 'src/features/accessKey/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface DeleteAccessKeyProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccessKey?: AccessKey;
    children?: React.ReactNode;
}

function DeleteAccessKey<TData>({ selectedAccessKey, children }: DeleteAccessKeyProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteAccessKey } = useDeleteAccessKey({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCESS_KEYS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(accessKeyMessages.deleteAccessKeySuccess),
                description: formatMessage(accessKeyMessages.deleteAccessKeySuccessDescription, {
                    entry: selectedAccessKey
                        ? formatMessage(ACCESS_KEY_TYPE_TRANSLATION_MAP[selectedAccessKey?.type])
                        : '',
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...accessKeyMessages.deleteAccessKey },
                subTitle: {
                    ...accessKeyMessages.deleteAccessKeySubtitle,
                    values: {
                        entry: selectedAccessKey
                            ? formatMessage(ACCESS_KEY_TYPE_TRANSLATION_MAP[selectedAccessKey?.type])
                            : '-',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...accessKeyMessages.confirmDeleteAccessKey,
                    values: {
                        entry: selectedAccessKey
                            ? formatMessage(ACCESS_KEY_TYPE_TRANSLATION_MAP[selectedAccessKey?.type])
                            : '-',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (selectedAccessKey) {
                        return (deleteAccessKey({ id: selectedAccessKey?.id || '', parkingId }), setIsModalOpen(false));
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteAccessKey;
